import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { BRAND_MINT } from '@/lib/utils'
import {
  getAdminDashboardApi,
  getSystemHealthApi,
  type AdminDashboard,
  type SystemHealth,
} from '@/lib/api'
import {
  LoaderIcon,
  UsersIcon,
  BuildingIcon,
  BanknoteIcon,
  ShieldCheckIcon,
  ActivityIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  AlertTriangleIcon,
  ServerIcon,
  DatabaseIcon,
  CpuIcon,
  HardDriveIcon,
  RefreshCwIcon,
} from 'lucide-react'

function formatNumber(n: number): string {
  return n.toLocaleString()
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const parts: string[] = []
  if (d > 0) parts.push(`${d}d`)
  if (h > 0) parts.push(`${h}h`)
  parts.push(`${m}m`)
  return parts.join(' ')
}

export const Route = createFileRoute('/dashboard/admin/')({
  component: () => {
    const [dashboard, setDashboard] = useState<AdminDashboard | null>(null)
    const [health, setHealth] = useState<SystemHealth | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
      let cancelled = false

      async function load() {
        try {
          const [dashRes, healthRes] = await Promise.all([
            getAdminDashboardApi(),
            getSystemHealthApi(),
          ])
          if (!cancelled) {
            setDashboard(dashRes)
            setHealth(healthRes)
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(
              err instanceof Error ? err.message : 'Failed to load admin dashboard',
            )
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }

      load()
      return () => { cancelled = true }
    }, [])

    if (loading) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
              />
              <h1 className="text-2xl font-semibold">Admin overview</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading dashboard…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div>
              <h1 className="text-2xl font-semibold">Admin overview</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Platform-wide statistics and system health.
              </p>
            </div>
          </div>
        </div>

        {dashboard && (
          <>
            {/* Key metrics row */}
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard
                label="Total users"
                value={formatNumber(dashboard.users.total)}
                subtitle={`${formatNumber(dashboard.users.new_this_month)} new this month`}
                icon={UsersIcon}
                color="text-blue-500"
              />
              <MetricCard
                label="Organizations"
                value={formatNumber(dashboard.organizations.total)}
                subtitle={`${formatNumber(dashboard.organizations.active)} active`}
                icon={BuildingIcon}
                color="text-emerald-500"
              />
              <MetricCard
                label="Funding Circles"
                value={formatNumber(dashboard.circles.total)}
                subtitle={`${formatNumber(dashboard.circles.total_participants)} participants`}
                icon={BanknoteIcon}
                color="text-amber-500"
              />
              <MetricCard
                label="Pending verifications"
                value={formatNumber(dashboard.pending_verifications)}
                subtitle="organizations waiting"
                icon={ShieldCheckIcon}
                color={dashboard.pending_verifications > 0 ? 'text-destructive' : 'text-muted-foreground'}
              />
            </div>

            {/* Org status breakdown */}
            <section className="mb-6">
              <SectionHeading>Organization Status</SectionHeading>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <MiniStatCard
                  label="Active"
                  value={formatNumber(dashboard.organizations.active)}
                  icon={CheckCircleIcon}
                  color="text-emerald-500"
                />
                <MiniStatCard
                  label="Pending"
                  value={formatNumber(dashboard.organizations.pending)}
                  icon={ClockIcon}
                  color="text-amber-500"
                />
                <MiniStatCard
                  label="Rejected"
                  value={formatNumber(dashboard.organizations.rejected)}
                  icon={XCircleIcon}
                  color="text-destructive"
                />
              </div>
            </section>

            {/* Circle status breakdown */}
            <section className="mb-6">
              <SectionHeading>Circle Status</SectionHeading>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <MiniStatCard
                  label="Open"
                  value={formatNumber(dashboard.circles.open)}
                  icon={ActivityIcon}
                  color="text-blue-500"
                />
                <MiniStatCard
                  label="Full"
                  value={formatNumber(dashboard.circles.full)}
                  icon={CheckCircleIcon}
                  color="text-emerald-500"
                />
                <MiniStatCard
                  label="Paying"
                  value={formatNumber(dashboard.circles.paying)}
                  icon={BanknoteIcon}
                  color="text-amber-500"
                />
                <MiniStatCard
                  label="Completed"
                  value={formatNumber(dashboard.circles.completed)}
                  icon={CheckCircleIcon}
                  color="text-emerald-500"
                />
                <MiniStatCard
                  label="Cancelled"
                  value={formatNumber(dashboard.circles.cancelled)}
                  icon={XCircleIcon}
                  color="text-muted-foreground"
                />
              </div>
            </section>

            {/* Transaction summary */}
            <section className="mb-6">
              <SectionHeading>Transaction Summary</SectionHeading>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <MiniStatCard
                  label="Total transactions"
                  value={formatNumber(dashboard.transactions.completed_count)}
                  icon={BanknoteIcon}
                  color="text-blue-500"
                />
                <MiniStatCard
                  label="Total volume"
                  value={`$${dashboard.transactions.total_volume}`}
                  icon={BanknoteIcon}
                  color="text-emerald-500"
                />
              </div>
            </section>
          </>
        )}

        {/* System Health */}
        {health && (
          <section>
            <SectionHeading>System Health</SectionHeading>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-4 flex items-center gap-3">
                <div
                  className={`flex size-10 items-center justify-center rounded-lg ${
                    health.status === 'healthy'
                      ? 'bg-emerald-500/10 text-emerald-600'
                      : health.status === 'degraded'
                        ? 'bg-amber-500/10 text-amber-600'
                        : 'bg-destructive/10 text-destructive'
                  }`}
                >
                  <ServerIcon className="size-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        health.status === 'healthy'
                          ? 'bg-emerald-500/10 text-emerald-600'
                          : health.status === 'degraded'
                            ? 'bg-amber-500/10 text-amber-600'
                            : 'bg-destructive/10 text-destructive'
                      }`}
                    >
                      <ActivityIcon className="size-3" />
                      {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Uptime {formatUptime(health.uptime)}
                  </p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <HealthField
                  icon={<DatabaseIcon className="size-4" />}
                  label="Database"
                  value={health.database.connected ? 'Connected' : 'Disconnected'}
                  status={health.database.connected ? 'ok' : 'error'}
                />
                <HealthField
                  icon={<DatabaseIcon className="size-4" />}
                  label="Tables"
                  value={String(health.database.table_count)}
                />
                <HealthField
                  icon={<CpuIcon className="size-4" />}
                  label="Node version"
                  value={health.node_version}
                />
                <HealthField
                  icon={<HardDriveIcon className="size-4" />}
                  label="Memory"
                  value={`${health.memory_usage_mb} MB`}
                />
                <HealthField
                  icon={<RefreshCwIcon className="size-4" />}
                  label="Platform"
                  value={health.platform}
                />
              </div>
            </div>
          </section>
        )}

        {!dashboard && !health && (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <AlertTriangleIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">Could not load dashboard</p>
            <p className="text-sm text-muted-foreground">
              Use the sidebar to manage organizations and users.
            </p>
          </div>
        )}
      </div>
    )
  },
})

// ── Sub-components ──────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2.5">
      <div className="flex items-center gap-[3px]" aria-hidden="true">
        <span
          className="inline-block w-[2px] h-3.5"
          style={{ backgroundColor: BRAND_MINT }}
        />
        <span
          className="inline-block rounded-full"
          style={{ width: '4px', height: '4px', backgroundColor: BRAND_MINT }}
        />
      </div>
      <h2 className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-[0.12em] select-none">
        {children}
      </h2>
    </div>
  )
}

function MetricCard({
  label,
  value,
  subtitle,
  icon: Icon,
  color,
}: {
  label: string
  value: string
  subtitle: string
  icon: React.ElementType
  color: string
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2">
        <Icon className={`size-5 shrink-0 ${color}`} />
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <span className="text-2xl font-semibold tabular-nums">{value}</span>
      <span className="text-xs text-muted-foreground">{subtitle}</span>
    </div>
  )
}

function MiniStatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: string
  icon: React.ElementType
  color: string
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
      <Icon className={`size-5 shrink-0 ${color}`} />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-base font-semibold tabular-nums">{value}</p>
      </div>
    </div>
  )
}

function HealthField({
  icon,
  label,
  value,
  status,
}: {
  icon: React.ReactNode
  label: string
  value: string
  status?: 'ok' | 'error'
}) {
  return (
    <div className="flex items-center gap-3">
      <div className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${
        status === 'error' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'
      }`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
          {label}
        </p>
        <p className={`truncate text-sm font-medium ${
          status === 'error' ? 'text-destructive' : 'text-foreground'
        }`}>
          {value}
        </p>
      </div>
    </div>
  )
}
